import { useMemo } from 'react';
import useAuth from '../auth/useAuth';
import { getBusinessTypeConfig, isModuleHiddenForBusinessType, getNavTitleOverride } from '../config/businessTypes';

/**
 * Resolves a business-type configuration — vocabulary, per-trade UI flags, and
 * which modules are hidden.
 *
 * By default it reads the signed-in merchant's type from AuthProvider. The
 * public booking page has no signed-in user, so it passes the slug explicitly
 * (from the restaurant payload) via `overrideSlug`. One hook, both sides —
 * otherwise the customer-facing page would need a parallel copy of this logic
 * and the two would drift.
 *
 *   const { terms, ui, isHidden } = useBusinessConfig();              // admin
 *   const { terms, ui } = useBusinessConfig(restaurant?.business_type); // public
 */
export default function useBusinessConfig(overrideSlug) {
  const { businessType: authBusinessType } = useAuth();

  // `undefined` means "not specified, fall back to auth". `null` is a real
  // answer — a restaurant whose type isn't set — and must not fall back, or a
  // public page would silently inherit whoever happens to be logged in.
  const businessType = overrideSlug === undefined ? authBusinessType : overrideSlug;

  return useMemo(() => {
    const config = getBusinessTypeConfig(businessType);

    return {
      businessType,
      label: config.label,
      terms: config.terms,
      ui: config.ui,
      hidden: config.hidden,
      isHidden: (moduleName) => isModuleHiddenForBusinessType(businessType, moduleName),
      navTitle: (moduleName) => getNavTitleOverride(businessType, moduleName)
    };
  }, [businessType]);
}
